import React, { useState } from 'react';
import { createPayPalSubscription } from '../services/api';
import { Loader } from 'lucide-react';

/**
 * Componente de suscripción recurrente con PayPal
 * Reemplaza PayPalCheckout para cobros automáticos recurrentes
 * 
 * @param {string} planId - ID del plan seleccionado
 * @param {string} planName - Nombre del plan para mostrar
 * @param {string} billingCycle - 'monthly' o 'annual'
 * @param {number} amount - Monto a pagar en MXN
 * @param {function} onSuccess - Callback cuando la suscripción es aprobada
 * @param {function} onError - Callback cuando hay un error
 * @param {function} onCancel - Callback cuando el usuario cancela
 */
export default function PayPalSubscriptionButton({ 
  planId, 
  planName,
  billingCycle, 
  amount,
  onSuccess, 
  onError,
  onCancel 
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [approvalUrl, setApprovalUrl] = useState(null);

  /**
   * Crear suscripción en PayPal
   */
  const handleSubscribe = async () => {
    try {
      setIsProcessing(true);
      console.log('💳 [Frontend] Creando suscripción PayPal...', { planId, billingCycle });

      const response = await createPayPalSubscription(planId, billingCycle);
      
      console.log('📦 [Frontend] Respuesta del backend:', response);

      if (!response.success || !response.approvalUrl) {
        console.error('❌ [Frontend] Respuesta inválida:', response);
        throw new Error(response.message || 'No se pudo crear la suscripción');
      }

      console.log('✅ [Frontend] Suscripción creada exitosamente:', response.subscriptionId);
      console.log('🔗 [Frontend] URL de aprobación:', response.approvalUrl);

      // Marcar que hay un pago pendiente
      localStorage.setItem('pending_paypal_payment', response.subscriptionId);
      
      // Redirigir al usuario a PayPal para aprobar la suscripción
      setApprovalUrl(response.approvalUrl);
      window.location.href = response.approvalUrl;

    } catch (error) {
      console.error('❌ [Frontend] Error creando suscripción:', error);
      setIsProcessing(false);
      onError?.(error.message || 'Error al iniciar la suscripción');
    }
  };

  return (
    <div className="w-full">
      {/* Información de la suscripción */}
      <div className="bg-morado/5 rounded-lg p-4 mb-4 border border-morado/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Plan seleccionado:</span>
          <span className="text-sm font-semibold text-morado">{planName}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Ciclo de facturación:</span>
          <span className="text-sm font-semibold text-morado">
            {billingCycle === 'annual' ? 'Anual' : 'Mensual'}
          </span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Tipo:</span>
          <span className="text-sm font-semibold text-green-600">
            🔄 Renovación Automática
          </span>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-morado/20">
          <span className="text-base font-bold text-gray-900">Total a pagar:</span>
          <span className="text-xl font-bold text-morado">
            ${amount.toFixed(2)} MXN
          </span>
        </div>
        <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
          <p className="text-xs text-blue-800">
            💡 <strong>Renovación automática:</strong> Tu suscripción se renovará automáticamente 
            cada {billingCycle === 'annual' ? 'año' : 'mes'}. Puedes cancelar en cualquier momento desde tu cuenta de PayPal.
          </p>
        </div>
      </div>

      {/* Botón de suscripción */}
      <button
        onClick={handleSubscribe}
        disabled={isProcessing || !!approvalUrl}
        className={`
          w-full py-3 px-6 rounded-lg font-semibold text-white
          transition-all duration-200
          ${isProcessing || approvalUrl
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-morado hover:opacity-90 shadow-lg hover:shadow-xl'
          }
          flex items-center justify-center gap-2
        `}
      >
        {isProcessing ? (
          <>
            <Loader className="animate-spin h-5 w-5" />
            <span>Procesando...</span>
          </>
        ) : approvalUrl ? (
          <>
            <span>Redirigiendo a PayPal...</span>
          </>
        ) : (
          <>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <span>Suscribirse con PayPal</span>
          </>
        )}
      </button>

      {/* Información adicional */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">
          Al hacer clic, serás redirigido a PayPal para completar tu suscripción.
        </p>
        <p className="text-xs text-gray-400 mt-1">
          🔒 Pago seguro procesado por PayPal
        </p>
      </div>
    </div>
  );
}

